export const param = (q: Record<string, string | number> = {}, allowNull = false) => {
  const search = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (allowNull || (v !== undefined && v !== null)) {
      search.append(k, v.toString());
    }
  });

  return search.toString();
};

/**
 * Extracts the domain from a given URL
 *
 * @param url - the url to process
 * @returns the domain of the provided URL
 */
export const getDomainFromURL = (url: string): string => {
  try {
    let newUrl = url;
    // Ensure the URL starts with a protocol to use URL constructor correctly
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      newUrl = `http://${url}`;
    }
    const urlObject = new URL(newUrl);

    return urlObject.hostname;
  } catch (_) {
    return '';
  }
};

/**
 * Gets just the domain + extension.  Prunes subdomains and `www`.
 *
 * @param urlString The url
 * @returns the hostname and extension URL only.
 */
export function getDomainAndExtension(urlString: string): string {
  const hostname = getDomainFromURL(urlString);
  if (!hostname) {
    return '';
  }

  const parts: string[] = hostname.split('.');

  // Check for multi-part TLDs like '.co.uk'
  if (
    parts.length > 2 &&
    (parts[parts.length - 2].length <= 2 || parts[parts.length - 1].length <= 2)
  ) {
    return parts.slice(-3).join('.');
  }

  return parts.slice(-2).join('.');
}

/**
 * The front-end version of the fix to not show secondary company directory websites
 * we use for company matching.
 *
 * @param url The url
 * @returns `true` if we should hide the URL because it is a company directory. `false` if we should show.
 */
export const isVisibleCompanyURL = (url: string): boolean => {
  const domain = getDomainAndExtension(url);
  if (!domain) {
    return false;
  }

  // The list of sites we accept that are NOT the main company site to allow for matches
  // Taken from this list:
  // https://github.com/Mercator-AI/mercator-gcp-data-engineering-task-containers/blob/main/dbt_enricher/seeds/company_matching/seed_business_directory_domain.csv
  const secondaryCompanyDomains = [
    'bbb.org',
    'bizapedia.com',
    'buildzoom.com',
    'buzzfile.com',
    'cobaltintelligence.com',
    'constructconnect.com',
    'corporationwiki.com',
    'crunchbase.com',
    'dallasbuilders.com',
    'dnb.com',
    'levelset.com',
    'linkedin.com',
    'mapquest.com',
    'opencorporates.com',
    'pitchbook.com',
    'procore.com',
    'yelp.ca',
    'yelp.com',
  ];
  const isSecondaryCompanyDomain = secondaryCompanyDomains.includes(domain);

  return !isSecondaryCompanyDomain;
};
